/* eslint-disable no-unused-expressions */
import { IBuildings, ILanguages, ISites } from '@llws/lift-entity-interfaces';
import { reactive } from 'vue';
import type { App } from 'vue';
import dayjs from 'dayjs';
import type { Emitter } from 'mitt';
import {
  CurrentUser,
  TLanguage,
  ISidebarHeader,
  ISelectOption,
  IRsSwal,
  IRsSwalConfig,
  IRouteMetaPermissionConfig,
  TAccessType,
  TDynamicEnv,
  IRouteMetaFooterExtraButton,
} from '@/shared';
import { ICurrentClient } from '@/shared/interfaces/current-client';
import { IGlobalBtnConfig } from '@/shared/interfaces/global-btn-config';
import { IGlobalCheckBoxConfig } from '@/shared/interfaces/global-checkbox-config';
import { IRootVueInstance } from '@/shared/interfaces/rentsync-vue-instance';
import { IEnvironment } from '@/shared/interfaces/environment';
import type { TUtilsMethods } from '@/plugins/utilities-plugin';
import { getFileLoc, siteIncludesProperty } from '@/shared/helpers';
import store from '@/store';
import i18n from '@/i18n';

const widthMapping = {
  small: 480,
  medium: 680,
  large: 880,
  default: 512,
};

// $rsGlobals is a Vue 2 standalone-instance-per-mixin pattern (`new Vue({ store, mixins: [rootMixin] })`)
// converted to a plain reactive object for Vue 3 — there is no component instance anymore, so `$store`,
// `$env`, `$eventBus`, and `$swal` (all previously inherited via the Vue prototype chain) are sourced
// directly from the `store` import and from the `app` instance's `config.globalProperties`.

// Vue 2's `initMethods` bound each instance method to the component, so `$rsGlobals` methods kept
// working when destructured or passed as callbacks (e.g. Home.vue's `getHomeDashboardKey` destructures
// `$userHasAtLeastOneAccess`). A plain reactive object does not auto-bind, so bind each own method back
// to the proxy to preserve that behavior; getters are left untouched (they have no own `value`).
const bindMethods = <T extends object>(target: T): T => {
  const record = target as unknown as Record<string, unknown>;
  Object.keys(target).forEach((key) => {
    const descriptor = Object.getOwnPropertyDescriptor(target, key);
    if (descriptor && typeof descriptor.value === 'function') {
      record[key] = (descriptor.value as (...args: unknown[]) => unknown).bind(target);
    }
  });
  return target;
};

export const createRsGlobals = (app: App): IRootVueInstance => bindMethods(reactive({
  $globalBtnDisabled: false as boolean,
  isBladeReady: false as boolean,
  resizeEventHandler: null,
  globalTranslationsConfig: false as boolean,
  globalSaveBtnConfig: {
    disabled: false,
    variant: 'primary',
    text: i18n.t('actions.save') as string,
    iconWeight: '',
    icon: '',
    disableToolTip: false,
    toolTipText: '',
    // eslint-disable-next-line no-console
    handler() { console.warn('global save button clicked. Set behaviour with $rsGlobals.$setGlobalSaveBtnConfig'); },
  } as IGlobalBtnConfig,
  globalDeleteBtnConfig: {
    disabled: false,
    variant: 'delete',
    icon: 'trash-alt',
    iconWeight: '',
    text: i18n.t('actions.delete') as string,
    // eslint-disable-next-line no-console
    handler() { console.warn('Global delete button clicked. Set behaviour with $rsGlobals.$setGlobalDeleteBtnConfig'); },
  } as IGlobalBtnConfig,
  globalTranslationsBtnConfig: {
    disabled: false,
    text: i18n.t('actions.translations') as string,
    handler() { console.warn('Global translations clicked. Set behaviour with $rsGlobals.$setGlobalTranslationsConfig'); },
  } as IGlobalBtnConfig,
  globalCheckBoxConfig: {
    checkBoxValue: false,
    disabled: false,
    text: '',
    trueValue: true,
    falseValue: false,
    // eslint-disable-next-line no-console
    handler() { console.warn('Global checkbox clicked. Set behaviour with $rsGlobals.$setGlobalCheckBoxConfig'); },
  } as IGlobalCheckBoxConfig,
  footerBottomText: '' as string,
  footerCustomText: '' as string,
  footerLastUpdated: '' as string,

  // In Vue 2 `$env` was inherited via the prototype chain; on the plain reactive object it must be
  // sourced explicitly from the app's global properties (set in utilities-plugin from window.APP_CONFIG).
  get $env(): IEnvironment {
    return app.config.globalProperties.$env;
  },

  // Like $env: `$utils` and `$eventBus` were inherited via the Vue 2 prototype chain and must be
  // re-exposed on the reactive object so `$rsGlobals.$utils` / `$rsGlobals.$eventBus` keep resolving.
  get $utils(): TUtilsMethods {
    return app.config.globalProperties.$utils;
  },

  get $eventBus(): Emitter<Record<string, unknown>> {
    return app.config.globalProperties.$eventBus;
  },

  get $currentUser(): CurrentUser {
    return store.getters['users/current'];
  },

  get $currentClient(): ICurrentClient {
    return store.getters['users/current']?.client;
  },

  get $corporateSite(): ISites | undefined {
    return this.$currentClient.sites?.find(
      (site) => site.parentClientId === this.$currentClient.id
        && site.sourceClientId === this.$currentClient.id,
    );
  },

  get $currentSiteId(): number | null {
    return store.getters.sidebarHeaderData?.data?.siteKey ? store.getters.sidebarHeaderData?.data?.id : null;
  },

  get $language(): TLanguage {
    return (store.state?.language || 'en') as TLanguage;
  },

  get $isTranslateMode(): boolean {
    return store.state?.language !== 'en';
  },

  get $sidebarHeaderData(): ISidebarHeader {
    return store.getters.sidebarHeaderData;
  },

  get $siteSourceClientId(): number {
    return store.getters.sidebarHeaderData?.data?.sourceClientId || this.$currentClient?.id;
  },

  get $siteUrl(): string {
    return (store.getters.sidebarHeaderData?.data?.siteUrl || '').replace(/(http:\/\/|https:\/\/)/g, '');
  },

  get $siteSourceClientKey(): string {
    return store.getters.sidebarHeaderData?.data?.siteKey || this.$currentClient?.clientKey;
  },

  get $siteSourceClientLanguages(): ILanguages[] {
    if (store.getters.sidebarHeaderData?.data?.languages) {
      return store.getters.sidebarHeaderData.data.languages;
    }

    if (this.$currentClient?.languages?.length) {
      return this.$currentClient.languages;
    }

    if (this.$currentClient.sites?.find((s) => s.sourceClientId === this.$currentClient.id)) {
      return this.$currentClient.sites?.find((s) => s.sourceClientId === this.$currentClient.id)?.languages ?? [];
    }

    return [];
  },

  get $clientLanguageOptions(): ISelectOption[] {
    return this.$convertLanguageToOptions(this.$siteSourceClientLanguages);
  },

  get $clientAndSiteLanguages(): ILanguages[] {
    if (store.getters.sidebarHeaderData?.data?.languages) {
      return store.getters.sidebarHeaderData.data.languages;
    }

    const clientAndSiteLanguages: Record<number, ILanguages> = {};
    const { languages, sites, ownedSites } = this.$currentClient;

    languages?.forEach((language) => {
      clientAndSiteLanguages[language.id!] = language;
    });

    [...(sites || []), ...(ownedSites || [])].forEach((site) => {
      site.languages?.forEach((language) => {
        clientAndSiteLanguages[language.id!] = language;
      });
    });

    return Object.values(clientAndSiteLanguages);
  },

  get $clientAndSiteLanguageOptions(): ISelectOption[] {
    return this.$convertLanguageToOptions(this.$clientAndSiteLanguages);
  },

  get $s3Url(): string {
    return store.state?.s3Url;
  },

  get $isIFrame(): boolean {
    return store.state?.isIFrame;
  },

  get $isClientChild(): boolean {
    return !!(this.$currentClient?.parentId && this.$currentClient?.hasChildPropertiesOnly === 1);
  },

  get $propertyAssetClientKey(): string {
    return this.$currentClient.clientKey;
  },

  get $propertyDetailsAssetClientKey(): string {
    const currentProperty: IBuildings | undefined = this.$sidebarHeaderData?.data as IBuildings | undefined;
    return (currentProperty && this.$isPropertyChild(currentProperty) && this.$currentClient.parent)
      ? this.$currentClient.parent!.clientKey
      : this.$currentClient.clientKey;
  },

  get $propertyDetailsClientId(): number | null {
    const currentProperty: IBuildings | undefined = this.$sidebarHeaderData?.data as IBuildings | undefined;
    return currentProperty ? currentProperty.clientId : null;
  },

  get $isListingScoresDisabled(): boolean {
    return this.$hasClientSettingValue('disable_listing_scores', '1');
  },

  get $isAdLayoutsDisabled(): boolean {
    return this.$hasClientSettingValue('disable_ad_layouts', '1');
  },

  get $isWebhooksDisabled(): boolean {
    return this.$hasClientSettingValue('disable_webhooks', '1');
  },

  get $isAutoRespondersDisabled(): boolean {
    return this.$hasClientSettingValue('disable_auto_responders', '1');
  },

  get $isQuickUpdateDisabled(): boolean {
    return this.$hasClientSettingValue('disable_quick_update', '1');
  },

  get $isFeatureChatbotEnabled(): boolean {
    return this.$hasClientSettingValue('enable_feature_chatbot', '1');
  },

  get $rsSwal(): IRsSwal {
    return {
      success: (rsSwalConfig: IRsSwalConfig) => {
        app.config.globalProperties.$swal.fire({
          icon: 'success',
          title: rsSwalConfig.title || i18n.t('swal.success') as string,
          text: rsSwalConfig.text || i18n.t('swal.operationSuccessful') as string,
          confirmButtonColor: rsSwalConfig.confirmButtonColor || '#00a889',
          customClass: rsSwalConfig.customClass,
        });
      },
      remove: (rsSwalConfig: IRsSwalConfig) => {
        app.config.globalProperties.$swal.fire({
          title: rsSwalConfig.title ? `<i class="fas fa-exclamation-triangle rs-text-warning"></i> ${rsSwalConfig.title}` : i18n.t('swal.areYouSure') as string,
          text: rsSwalConfig.text || i18n.t('swal.areYouSureRemove') as string,
          width: widthMapping[rsSwalConfig.width || 'default'],
          html: rsSwalConfig.html,
          showDenyButton: rsSwalConfig.showDenyButton !== undefined ? rsSwalConfig.showDenyButton : !rsSwalConfig.text,
          denyButtonText: rsSwalConfig.denyButtonText || i18n.t('actions.cancel') as string,
          cancelButtonText: rsSwalConfig.cancelButtonText || i18n.t('actions.cancel') as string,
          showCancelButton: rsSwalConfig.showCancelButton !== undefined ? rsSwalConfig.showCancelButton : true,
          showCloseButton: true,
          confirmButtonColor: '#F26175',
          confirmButtonText: rsSwalConfig.confirmButtonText || i18n.t('swal.delete') as string,
          customClass: {
            container: (!rsSwalConfig.text && !rsSwalConfig.html) ? 'swal-remove-container swal-remove-no-text' : 'swal-remove-container',
            htmlContainer: 'swal-remove-body-container',
            title: 'swal-remove-title',
            actions: 'swal-remove-actions',
            closeButton: 'swal-remove-close',
            ...rsSwalConfig.customClass,
          },
        }).then((response) => {
          if (response.isConfirmed && rsSwalConfig.confirmHandler) {
            rsSwalConfig.confirmHandler();
          } else if (rsSwalConfig.rejectHandler) {
            rsSwalConfig.rejectHandler();
          } else if (rsSwalConfig.confirmWithoutSaveHandler) {
            rsSwalConfig.confirmWithoutSaveHandler();
          }
        });
      },
      warning: (rsSwalConfig: IRsSwalConfig) => {
        app.config.globalProperties.$swal.fire({
          title: rsSwalConfig.title ? `<i class="fas fa-exclamation-triangle rs-icon-warning"></i> ${rsSwalConfig.title}` : 'No Title Inserted',
          text: rsSwalConfig.text,
          showDenyButton: !!((rsSwalConfig.showDenyButton || !rsSwalConfig.text)),
          denyButtonText: rsSwalConfig.denyButtonText || i18n.t('swal.continueWithoutSaving') as string,
          confirmButtonText: rsSwalConfig.confirmButtonText || i18n.t('swal.saveChanges') as string,
          cancelButtonText: rsSwalConfig.cancelButtonText || i18n.t('actions.cancel') as string,
          showCancelButton: rsSwalConfig.showCancelButton,
          showCloseButton: rsSwalConfig.showCloseButton !== undefined ? rsSwalConfig.showCloseButton : true,
          confirmButtonColor: rsSwalConfig.confirmButtonColor || '#00a889',
          allowOutsideClick: rsSwalConfig.allowOutsideClick !== undefined ? rsSwalConfig.allowOutsideClick : true,
          customClass: {
            container: !rsSwalConfig.text ? 'swal-warning-container swal-warning-no-text' : 'swal-warning-container',
            htmlContainer: 'swal-warning-body-container',
            title: 'swal-warning-title',
            actions: 'swal-warning-actions',
            closeButton: rsSwalConfig.showCloseButton === undefined || rsSwalConfig.showCloseButton ? 'swal-warning-close' : '',
            denyButton: rsSwalConfig.denyButtonClass || '',
            ...rsSwalConfig.customClass,
          },
        }).then((response) => {
          if (response.isConfirmed && rsSwalConfig.confirmHandler) {
            rsSwalConfig.confirmHandler();
          } else if (response.isDenied && rsSwalConfig.rejectHandler) {
            rsSwalConfig.rejectHandler();
          } else if (response.isDismissed && rsSwalConfig.dismissHandler) {
            rsSwalConfig.dismissHandler();
          } else if (rsSwalConfig.confirmWithoutSaveHandler) {
            rsSwalConfig.confirmWithoutSaveHandler();
          }
        });
      },
      info: (rsSwalConfig: IRsSwalConfig) => {
        app.config.globalProperties.$swal.fire({
          title: rsSwalConfig.title ? `<i class="fas fa-info-circle"></i> ${rsSwalConfig.title}` : 'No Title Inserted',
          text: rsSwalConfig.text,
          showDenyButton: !!((rsSwalConfig.showDenyButton || !rsSwalConfig.text)),
          denyButtonText: rsSwalConfig.denyButtonText || i18n.t('swal.continueWithoutSaving') as string,
          confirmButtonText: rsSwalConfig.confirmButtonText || i18n.t('swal.saveChanges') as string,
          showCancelButton: false, // no more cancel button for now
          confirmButtonColor: rsSwalConfig.confirmButtonColor || '#00a889',
          showCloseButton: true,
          customClass: {
            container: !rsSwalConfig.text ? 'swal-warning-container swal-warning-no-text' : 'swal-warning-container',
            htmlContainer: 'swal-warning-body-container',
            title: 'swal-warning-title',
            actions: 'swal-warning-actions',
            closeButton: 'swal-info-close',
            ...rsSwalConfig.customClass,
          },
        }).then((response) => {
          if (response.isConfirmed && rsSwalConfig.confirmHandler) {
            rsSwalConfig.confirmHandler();
          } else if (rsSwalConfig.rejectHandler) {
            rsSwalConfig.rejectHandler();
          } else if (rsSwalConfig.confirmWithoutSaveHandler) {
            rsSwalConfig.confirmWithoutSaveHandler();
          }
        });
      },
    };
  },

  get $clientHasRentalsBundle(): boolean {
    return !!this.$currentClient.serviceRentsync
      && !!this.$currentClient.hasRentalsBundle;
  },

  getFileLoc,

  $featureActive(featureName: string, featureValue: string = '1'): boolean {
    const envs = app.config.globalProperties.$env as TDynamicEnv;
    return envs[`VUE_APP_USE_${featureName.toUpperCase()}`] === featureValue;
  },

  $setGlobalSaveBtnConfig(config: IGlobalBtnConfig): void {
    this.globalSaveBtnConfig = {
      disabled: false,
      variant: 'primary',
      text: i18n.t('actions.save') as string,
      disableToolTip: true,
      toolTipText: '',
      iconWeight: '',
      icon: '',
      // eslint-disable-next-line no-console
      handler() { console.warn('global save button clicked. Set behaviour with $rsGlobals.$setGlobalSaveBtnConfig'); },
      ...config,
    };
  },

  $setGlobalDeleteBtnConfig(config: IGlobalBtnConfig): void {
    this.globalDeleteBtnConfig = {
      disabled: false,
      icon: 'trash-alt',
      iconWeight: '',
      variant: 'error',
      text: i18n.t('actions.delete') as string,
      // eslint-disable-next-line no-console
      handler() { console.warn('global delete button clicked. Set behaviour with $rsGlobals.$setGlobalDeleteBtnConfig'); },
      ...config,
    };
  },

  $setGlobalTranslationsConfig(config: boolean): void {
    this.globalTranslationsConfig = config;
  },

  $setGlobalTranslationsBtnConfig(config: IGlobalBtnConfig): void {
    this.globalTranslationsBtnConfig = {
      disabled: false,
      text: i18n.t('actions.translations') as string,
      variant: 'secondary',
      // eslint-disable-next-line no-console
      handler() { console.warn('global translations button clicked. Set behaviour with $rsGlobals.$setGlobalDeleteBtnConfig'); },
      ...config,
    };
  },

  $setGlobalCheckBoxConfig(config: IGlobalCheckBoxConfig): void {
    const mergedConfig: IGlobalCheckBoxConfig = {
      checkBoxValue: false,
      disabled: false,
      text: '',
      trueValue: true,
      falseValue: false,
      // eslint-disable-next-line no-console
      handler() { console.warn('global checkbox clicked. Set behaviour with $rsGlobals.$setGlobalCheckBoxConfig'); },
      ...config,
    };

    this.globalCheckBoxConfig = mergedConfig;
  },

  $setFooterExtraButton(config: Partial<IRouteMetaFooterExtraButton> & { buttonKey: string }) {
    app.config.globalProperties.$eventBus.emit('set-footer-extra-button', config);
  },

  $onBladeReady(callback: Function): void {
    setTimeout(() => {
      callback();
      this.isBladeReady = true;
    }, 500);
  },

  $userHasAccess(
    permissionKey: string | string[] | IRouteMetaPermissionConfig | IRouteMetaPermissionConfig[],
    accessType?: TAccessType,
    id?: number | string,
  ): boolean {
    if (this.$currentUser) {
      return this.$currentUser.hasPermissionAccess(permissionKey, accessType, id);
    }

    return false;
  },

  $userHasAtLeastOneAccess(
    permissionKey: string | string[] | IRouteMetaPermissionConfig | IRouteMetaPermissionConfig[],
    accessType?: TAccessType,
  ): boolean {
    if (this.$currentUser) {
      return this.$currentUser.hasAtLeastOneModel(permissionKey, accessType ?? 'read');
    }

    return false;
  },

  /**
   * Whether the CLIENT owns a product, e.g. `waitinglist`.
   *
   * Entitlement, not permission. Distinct from `$userHasAccess`, which answers what
   * this user may do: most non-admin groups at clients that own `waitinglist` carry
   * no read grant for it, so gating UI on permissions would hide the feature from
   * clients who pay for it.
   */
  $clientHasProduct(productKey: string): boolean {
    return (this.$currentUser?.clientProducts ?? []).includes(productKey);
  },

  $hasClientSettingValue(key: string, value: string): boolean {
    if (this.$currentUser) {
      return `${this.$currentUser.getClientSettingValue(key)}`.trim() === `${value}`.trim();
    }

    return false;
  },

  $hasPromotedListingsRestricted(): boolean {
    // Do not restrict Super Users
    if (this.$currentUser.isSuperUser) {
      return false;
    }
    const blackListed = this.$currentUser.getClientSettingValue('promoted_listings_blacklist') === '1';
    const limitedAccess = (
      !this.$userHasAtLeastOneAccess('promoted_listings', 'update')
      || !this.$userHasAtLeastOneAccess('standard_listings', 'update')
    );
    // Restrict when current client has the promoted_listings_blacklist feature flag, or does not have permission
    return blackListed || limitedAccess;
  },

  /**
   * Whether the unit website visibility action applies: the user can update the building and
   * the building is served by at least one of the client's sites.
   */
  $hasWebsiteVisibilityAvailable(building?: IBuildings): boolean {
    if (!building || !this.$userHasAccess('buildings', 'update', building.id!)) {
      return false;
    }
    return [...(this.$currentClient.ownedSites ?? []), ...(this.$currentClient.sites ?? [])]
      .some((site) => siteIncludesProperty(site, building));
  },

  /**
   * Whether the unit listings exclusion action applies: the building is syndicated to at least
   * one listing partner and the user can update its standard listings.
   */
  $hasExcludeSyndicationAvailable(building?: IBuildings): boolean {
    if (!building || !building.rentsyncProperties?.some((rentsyncProperty) => rentsyncProperty.state === 1)) {
      return false;
    }
    return this.$userHasAccess('standard_listings', 'update', building.id!);
  },

  /**
   * Whether the client has switched unit-level listing exclusions off account-wide via the
   * "Disable 'Exclude Unit from Listings' for all properties" setting in Listing Settings.
   * While it is on, units must not be excluded again one at a time.
   *
   * Reads what `client-syndication-settings/getUnitExclusionsDisabled` last loaded, so views
   * that rely on it have to dispatch that action.
   */
  $hasUnitExclusionsDisabled(): boolean {
    return store.getters['client-syndication-settings/unitExclusionsDisabled'] === true;
  },

  $hasToursRestricted(): boolean {
    if (this.$currentUser.isSuperUser) {
      return false;
    }
    return !this.$userHasAtLeastOneAccess('tour_admins') && this.$userHasAtLeastOneAccess('tour_agents');// Restricted for tour agents
  },

  $isLanguage(languageCode: TLanguage): boolean {
    return (store.state.language || 'en') === languageCode;
  },

  $isPropertyChild(property?: IBuildings): boolean {
    return !!property?.childClientId && property?.childClientId === this.$currentClient.id;
  },

  $setFooterBottomText(text: string) {
    this.footerBottomText = text;
  },
  $setFooterCustomText(text: string) {
    this.footerCustomText = text;
  },

  $setFooterLastUpdated(date: Date | null) {
    if (dayjs(date).isValid() && date !== null) {
      const isToday = dayjs().isSame(dayjs(date), 'day');
      if (isToday) {
        this.footerLastUpdated = i18n.t('dates.today') as string;
      } else {
        const locale = i18n.locale === 'fr' ? 'fr-CA' : 'en-US';
        this.footerLastUpdated = date.toLocaleString(
          locale,
          {
            month: 'short', year: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true,
          },
        );
      }
    } else if (date === null) {
      this.footerLastUpdated = '';
    } else {
      console.error('$setFooterLastUpdate excepts argument of type Date only');
    }
  },

  $convertLanguageToOptions(languagesList: ILanguages[]) {
    const languages: ISelectOption[] = languagesList
      .map((language: ILanguages) => ({
        text: language.languageCode!.toUpperCase(),
        value: language.languageCode,
      } as ISelectOption));

    languages.unshift({ text: 'EN', value: 'en' });
    return languages;
  },
}) as IRootVueInstance);

export type TRsGlobals = ReturnType<typeof createRsGlobals>;

export default createRsGlobals;
